import { create } from "@most/create";
import { isNil, mergeRight } from "ramda";
import { notBlank } from "./general";

export class AjaxError extends Error {
  constructor(response, message = null) {
    super(response?.statusText ?? {});
    this.response = response;
    this.message = notBlank(message) ? message : null;
  }
}

const defaultAjaxRequest = {
  url: "",
  body: null,
  user: null,
  async: true,
  method: "GET",
  headers: {},
  // timeout: 0,
  password: null,
  // hasContent: boolean;
  // crossDomain?: boolean; //true if a cross domain request, else false
  withCredentials: false,
  createXhr: null,
  responseType: "json",
};

const getDefaultAjaxRequest = ajaxRequest =>
  mergeRight(
    defaultAjaxRequest,
    typeof ajaxRequest === "string" ? { url: ajaxRequest } : ajaxRequest
  );

const createXhr = ajaxRequest => {
  if (typeof ajaxRequest.createXhr === "function") {
    return ajaxRequest.createXhr(ajaxRequest);
  } else {
    const xhr = new XMLHttpRequest();

    //IE 11 doesn't like setting the timeout before open is called, removing the option for now
    // xhr.timeout = ajaxRequest.timeout;

    xhr.withCredentials = ajaxRequest.withCredentials;

    // IE doesn't support json as responseTYpe
    // xhr.responseType = ajaxRequest.responseType;
    if (ajaxRequest.responseType !== "json") {
      xhr.responseType = ajaxRequest.responseType;
    }

    return xhr;
  }
};

const createRequestOnSubscription =
  (ajaxRequest, xhr, progressEventHandler) => (add, end, error) => {
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (ajaxRequest.responseType === "json") {
            add(JSON.parse(xhr.response || xhr.responseText || "null"));
          } else {
            add(xhr.responseType === "text" ? xhr.resonseText : xhr.response);
          }

          end();
        } else if (xhr.status === 413) {
          error(new AjaxError("Submission Payload too large."));
        } else {
          const res = notBlank(xhr.responseText)
            ? xhr.responseText
            : notBlank(xhr.response)
            ? xhr.response
            : null;
          const response = JSON.parse(res);
          error(new AjaxError(xhr.response, response?.message));
        }
      }
    };

    if (!isNil(xhr.upload) && !isNil(progressEventHandler)) {
      xhr.upload.onprogress = progressEventHandler;
    }

    xhr.open(
      ajaxRequest.method,
      ajaxRequest.url,
      ajaxRequest.async,
      ajaxRequest.user,
      ajaxRequest.password
    );

    Object.keys(ajaxRequest.headers).forEach(header => {
      xhr.setRequestHeader(header, ajaxRequest.headers[header]);
    });

    xhr.send(ajaxRequest.body);

    return () => {
      if (typeof xhr.abort === "function" && xhr.readyState !== 4) {
        xhr.abort();
      }
    };
  };

export const ajax = ajaxRequest => {
  const defaultAjaxRequest = getDefaultAjaxRequest(ajaxRequest);
  return create(
    createRequestOnSubscription(
      defaultAjaxRequest,
      createXhr(defaultAjaxRequest)
    )
  );
};

export const ajaxWithProgress = ajaxRequest => {
  let progressAdd;

  //TODO: use most-subject to create the progress stream
  const progressOnSubscription = (add, end, error) => {
    progressAdd = add;
  };

  const progressEventHandler = e => {
    progressAdd(e);
  };

  const defaultAjaxRequest = getDefaultAjaxRequest(ajaxRequest);
  const onSubscription = createRequestOnSubscription(
    defaultAjaxRequest,
    createXhr(defaultAjaxRequest),
    progressEventHandler
  );

  return [create(onSubscription), create(progressOnSubscription)];
};
